import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBug, faLightbulb, faChartLine, faQuestionCircle, faFrown, faCommentDots } from '@fortawesome/free-solid-svg-icons';
import { api } from '../services/api';
import '../styles/FeedbackDetail.css';

interface FeedbackComment {
  id: string;
  content: string;
  isAdminComment: boolean;
  createdAt: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    profilePicture: string | null;
    platformRole: string;
  };
}

interface FeedbackDetail {
  id: string;
  type: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  category: string | null;
  upvotes: number;
  views: number;
  adminResponse: string | null;
  respondedAt: string | null;
  browser: string | null;
  device: string | null;
  os: string | null;
  url: string | null;
  screenshot: string | null;
  createdAt: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    profilePicture: string | null;
  };
  comments: FeedbackComment[];
  votes: Array<{ userId: string }>;
  _count: {
    comments: number;
    votes: number;
  };
}

const FeedbackDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState<FeedbackDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [adminResponse, setAdminResponse] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [newPriority, setNewPriority] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  
  const currentUserId = localStorage.getItem('userId');
  const userRole = localStorage.getItem('platformRole');
  const isAdmin = userRole === 'PLATFORM_ADMIN';

  useEffect(() => {
    loadFeedback();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadFeedback = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/feedback/${id}`);
      setFeedback(response.data);
      setNewStatus(response.data.status);
      setNewPriority(response.data.priority);
      setAdminResponse(response.data.adminResponse || '');
    } catch (error) {
      console.error('Error loading feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async () => {
    try {
      await api.post(`/feedback/${id}/vote`);
      loadFeedback();
    } catch (error) {
      console.error('Error voting:', error);
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim()) return;

    setIsSubmittingComment(true);
    try {
      await api.post(`/feedback/${id}/comments`, { content: comment.trim() });
      setComment('');
      loadFeedback();
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleUpdateFeedback = async () => {
    setIsUpdating(true);
    try {
      await api.patch(`/feedback/${id}`, {
        status: newStatus,
        priority: newPriority,
        adminResponse: adminResponse.trim() || undefined
      });
      loadFeedback();
      alert('Feedback updated successfully');
    } catch (error) {
      console.error('Error updating feedback:', error);
      alert('Failed to update feedback');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this feedback?')) return;

    try {
      await api.delete(`/feedback/${id}`);
      navigate('/feedback');
    } catch (error) {
      console.error('Error deleting feedback:', error);
      alert('Failed to delete feedback');
    }
  };

  const getTypeIcon = (type: string) => {
    const icons: Record<string, any> = {
      BUG_REPORT: faBug,
      FEATURE_REQUEST: faLightbulb,
      IMPROVEMENT: faChartLine,
      QUESTION: faQuestionCircle,
      COMPLAINT: faFrown,
      OTHER: faCommentDots
    };
    return <FontAwesomeIcon icon={icons[type] || faCommentDots} />;
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { color: string; label: string }> = {
      NEW: { color: '#667eea', label: 'New' },
      IN_REVIEW: { color: '#f6ad55', label: 'In Review' },
      PLANNED: { color: '#4299e1', label: 'Planned' },
      IN_PROGRESS: { color: '#9f7aea', label: 'In Progress' },
      COMPLETED: { color: '#48bb78', label: 'Completed' },
      REJECTED: { color: '#f56565', label: 'Rejected' },
      CLOSED: { color: '#718096', label: 'Closed' }
    };
    const badge = badges[status] || { color: '#718096', label: status };
    return (
      <span className="status-badge" style={{ backgroundColor: badge.color }}>
        {badge.label}
      </span>
    );
  };

  const hasVoted = () => {
    return feedback?.votes?.some(v => v.userId === currentUserId) || false;
  };

  if (loading) {
    return <div className="loading-state">Loading feedback details...</div>;
  }

  if (!feedback) {
    return <div className="error-state">Feedback not found</div>;
  }

  const canEdit = feedback.user.id === currentUserId || isAdmin;

  return (
    <div className="feedback-detail-page">
      <button className="btn-back" onClick={() => navigate('/feedback')}>
        ← Back to Feedback
      </button>

      <div className="feedback-detail-container">
        <div className="feedback-main">
          <div className="feedback-header-detail">
            <div className="feedback-meta-detail">
              <span className="type-icon-large">{getTypeIcon(feedback.type)}</span>
              {getStatusBadge(feedback.status)}
              {feedback.priority !== 'LOW' && (
                <span className="priority-badge" style={{ 
                  backgroundColor: 
                    feedback.priority === 'CRITICAL' ? '#9f1239' :
                    feedback.priority === 'HIGH' ? '#f56565' :
                    '#f6ad55'
                }}>
                  {feedback.priority === 'CRITICAL' ? '🚨' : 
                   feedback.priority === 'HIGH' ? '🔥' : '⚠️'} {feedback.priority}
                </span>
              )}
            </div>
            <h1 className="feedback-title-detail">{feedback.title}</h1>
            {feedback.category && (
              <span className="category-tag">📁 {feedback.category}</span>
            )}
          </div>

          <div className="feedback-author-info">
            <div className="author-details">
              {feedback.user.profilePicture ? (
                <img src={feedback.user.profilePicture} alt="" className="author-avatar" />
              ) : (
                <div className="author-avatar-placeholder">
                  {feedback.user.firstName[0]}{feedback.user.lastName[0]}
                </div>
              )}
              <div>
                <div className="author-name">
                  {feedback.user.firstName} {feedback.user.lastName}
                </div>
                <div className="feedback-date">
                  {new Date(feedback.createdAt).toLocaleString()}
                </div>
              </div>
            </div>

            <div className="feedback-stats-detail">
              <div className="stat-item">
                <button 
                  className={`vote-button-large ${hasVoted() ? 'voted' : ''}`}
                  onClick={handleVote}
                >
                  ▲ {feedback.upvotes}
                </button>
              </div>
              <div className="stat-item">
                💬 {feedback._count.comments}
              </div>
              <div className="stat-item">
                👁️ {feedback.views}
              </div>
            </div>
          </div>

          <div className="feedback-description-detail">
            <h3>Description</h3>
            <p>{feedback.description}</p>
          </div>

          {feedback.screenshot && (
            <div className="feedback-screenshot">
              <h3>Screenshot</h3>
              <img src={feedback.screenshot} alt="Feedback screenshot" />
            </div>
          )}

          {(feedback.browser || feedback.device || feedback.os || feedback.url) && (
            <details className="system-info-section">
              <summary>📊 System Information</summary>
              <div className="system-info-grid">
                {feedback.browser && (
                  <div className="info-item">
                    <strong>Browser:</strong>
                    <span>{feedback.browser}</span>
                  </div>
                )}
                {feedback.device && (
                  <div className="info-item">
                    <strong>Device:</strong>
                    <span>{feedback.device}</span>
                  </div>
                )}
                {feedback.os && (
                  <div className="info-item">
                    <strong>OS:</strong>
                    <span>{feedback.os}</span>
                  </div>
                )}
                {feedback.url && (
                  <div className="info-item">
                    <strong>URL:</strong>
                    <a href={feedback.url} target="_blank" rel="noopener noreferrer">
                      {feedback.url}
                    </a>
                  </div>
                )}
              </div>
            </details>
          )}

          {feedback.adminResponse && (
            <div className="admin-response-section">
              <h3>🛡️ Admin Response</h3>
              <p>{feedback.adminResponse}</p>
              {feedback.respondedAt && (
                <div className="response-date">
                  Responded on {new Date(feedback.respondedAt).toLocaleString()}
                </div>
              )}
            </div>
          )}

          {canEdit && (
            <div className="actions-section">
              <button className="btn-delete" onClick={handleDelete}>
                🗑️ Delete Feedback
              </button>
            </div>
          )}

          {/* Comments Section */}
          <div className="comments-section">
            <h3>💬 Comments ({feedback.comments.length})</h3>
            
            <form onSubmit={handleAddComment} className="comment-form">
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add a comment..."
                rows={3}
                maxLength={1000}
              />
              <div className="comment-form-actions">
                <span className="char-count">{comment.length}/1000</span>
                <button type="submit" disabled={isSubmittingComment || !comment.trim()}>
                  {isSubmittingComment ? 'Posting...' : 'Post Comment'}
                </button>
              </div>
            </form>

            <div className="comments-list">
              {feedback.comments.map((c) => (
                <div 
                  key={c.id} 
                  className={`comment-item ${c.isAdminComment ? 'admin-comment' : ''}`}
                >
                  <div className="comment-header">
                    <div className="comment-author">
                      {c.user.profilePicture ? (
                        <img src={c.user.profilePicture} alt="" />
                      ) : (
                        <div className="comment-avatar">
                          {c.user.firstName[0]}{c.user.lastName[0]}
                        </div>
                      )}
                      <div>
                        <div className="comment-author-name">
                          {c.user.firstName} {c.user.lastName}
                          {c.isAdminComment && <span className="admin-badge">ADMIN</span>}
                        </div>
                        <div className="comment-date">
                          {new Date(c.createdAt).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                  <p className="comment-content">{c.content}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Admin Panel */}
        {isAdmin && (
          <div className="feedback-admin-panel">
            <h3>🛡️ Admin Controls</h3>
            
            <div className="admin-form-group">
              <label>Status</label>
              <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
                <option value="NEW">New</option>
                <option value="IN_REVIEW">In Review</option>
                <option value="PLANNED">Planned</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="COMPLETED">Completed</option>
                <option value="REJECTED">Rejected</option>
                <option value="CLOSED">Closed</option>
              </select>
            </div>

            <div className="admin-form-group">
              <label>Priority</label>
              <select value={newPriority} onChange={(e) => setNewPriority(e.target.value)}>
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="CRITICAL">Critical</option>
              </select>
            </div>

            <div className="admin-form-group">
              <label>Admin Response</label>
              <textarea
                value={adminResponse}
                onChange={(e) => setAdminResponse(e.target.value)}
                placeholder="Write a response to the user..."
                rows={6}
                maxLength={2000}
              />
              <span className="char-count">{adminResponse.length}/2000</span>
            </div>

            <button 
              className="btn-update-admin" 
              onClick={handleUpdateFeedback}
              disabled={isUpdating}
            >
              {isUpdating ? 'Updating...' : 'Update Feedback'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackDetailPage;
